import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X, Check, User, Link2, ArrowRight } from 'lucide-react';
import type { Character, CharacterAppearance, CharacterRelation } from '../../types';
import { COMMON_RELATION_TYPES } from '@xiaotianshu/shared';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { notifyError, errorMessage } from '../../utils/notify';
import './styles/characterRelations.css';

interface Props {
  bookId: string;
  character: Character;
  /** 全书人物（用于选择关系另一端） */
  allCharacters: Character[];
}

type Tab = 'relations' | 'appearances';

/**
 * 人物关系 + 出场反查（嵌入资料库的人物详情面板）。
 *
 * - 关系：有向边（1 条记录 + bidirectional 标记），避免双向存两条导致半条残留
 * - 出场：复用第 3 步-B 的 pg_trgm 检索反查整部作品正文，
 *   不额外建倒排表，规避"改名后索引失效"的一致性问题
 */
export function CharacterRelationsPanel({ bookId, character, allCharacters }: Props) {
  const { handleRequestChapterSwitch } = useWorkspace();
  const [tab, setTab] = useState<Tab>('relations');
  const [relations, setRelations] = useState<CharacterRelation[]>([]);
  const [appearances, setAppearances] = useState<CharacterAppearance[] | null>(null);
  const [appearanceMeta, setAppearanceMeta] = useState<{ total: number; first: number | null; last: number | null }>({ total: 0, first: null, last: null });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ toCharacterId: '', relationType: '师徒', bidirectional: false, note: '' });
  const [saving, setSaving] = useState(false);

  const loadRelations = useCallback(async () => {
    try {
      const rows = await window.api.getCharacterRelations(bookId);
      setRelations(rows);
    } catch (err) {
      notifyError('关系加载失败：' + errorMessage(err));
    }
  }, [bookId]);

  useEffect(() => { void loadRelations(); }, [loadRelations]);

  const loadAppearances = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await window.api.getCharacterAppearances(character.id);
      setAppearances(rows);
      setAppearanceMeta({
        total: rows.reduce((s, r) => s + r.count, 0),
        first: rows.length > 0 ? rows[0].chapter_number : null,
        last: rows.length > 0 ? rows[rows.length - 1].chapter_number : null,
      });
    } catch (err) {
      notifyError('出场反查失败：' + errorMessage(err));
      setAppearances([]);
    } finally {
      setLoading(false);
    }
  }, [character.id]);

  // 首次切到出场页时再查（避免每次打开人物详情都做全书检索）
  useEffect(() => {
    if (tab === 'appearances' && appearances === null) void loadAppearances();
  }, [tab, appearances, loadAppearances]);

  useEffect(() => {
    // 切换人物时重置
    setAppearances(null);
    setShowForm(false);
    setTab('relations');
  }, [character.id]);

  /** 与当前人物相关的边（含反向边，标注方向） */
  const related = useMemo(() => relations
    .filter((r) => r.from_character_id === character.id || r.to_character_id === character.id)
    .map((r) => {
      const outgoing = r.from_character_id === character.id;
      return {
        relation: r,
        outgoing,
        otherName: outgoing ? (r.to_character_name ?? '（已删除）') : (r.from_character_name ?? '（已删除）'),
      };
    }), [relations, character.id]);

  const candidates = useMemo(
    () => allCharacters.filter((c) => c.id !== character.id),
    [allCharacters, character.id],
  );

  const submit = async () => {
    if (!form.toCharacterId) {
      notifyError('请选择关系对象');
      return;
    }
    if (!form.relationType.trim()) {
      notifyError('请填写关系类型');
      return;
    }
    setSaving(true);
    try {
      await window.api.createCharacterRelation({
        bookId,
        fromCharacterId: character.id,
        toCharacterId: form.toCharacterId,
        relationType: form.relationType.trim(),
        bidirectional: form.bidirectional,
        note: form.note,
      });
      setShowForm(false);
      setForm({ toCharacterId: '', relationType: '师徒', bidirectional: false, note: '' });
      await loadRelations();
    } catch (err) {
      notifyError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const removeRelation = async (id: string) => {
    try {
      await window.api.deleteCharacterRelation(id);
      await loadRelations();
    } catch (err) {
      notifyError('删除关系失败：' + errorMessage(err));
    }
  };

  return (
    <div className="cr-panel">
      <div className="cr-head">
        <div className="cr-tabs" role="tablist">
          <button
            className={`cr-tab${tab === 'relations' ? ' is-active' : ''}`}
            onClick={() => setTab('relations')}
            role="tab"
            aria-selected={tab === 'relations'}
          >
            <Link2 size={13} /> 人物关系
            <span className="cr-tab-count">{related.length}</span>
          </button>
          <button
            className={`cr-tab${tab === 'appearances' ? ' is-active' : ''}`}
            onClick={() => setTab('appearances')}
            role="tab"
            aria-selected={tab === 'appearances'}
          >
            <User size={13} /> 出场章节
          </button>
        </div>
        {/* 当前人物名（面板自带，便于断言与读屏） */}
        <span className="cr-self" data-testid="cr-self-name">{character.name}</span>
        {tab === 'relations' && !showForm && (
          <button className="cr-add" onClick={() => setShowForm(true)} title="新增关系">
            <Plus size={13} /> 新增
          </button>
        )}
      </div>

      {tab === 'relations' ? (
        <>
          {showForm && (
            <div className="cr-form">
              <div className="cr-form-row">
                <select
                  className="cr-select"
                  value={form.toCharacterId}
                  onChange={(e) => setForm({ ...form, toCharacterId: e.target.value })}
                  aria-label="关系对象"
                >
                  <option value="">选择人物…</option>
                  {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input
                  className="cr-input"
                  list="cr-relation-types"
                  placeholder="关系（如 师徒）"
                  value={form.relationType}
                  onChange={(e) => setForm({ ...form, relationType: e.target.value })}
                  aria-label="关系类型"
                />
                <datalist id="cr-relation-types">
                  {COMMON_RELATION_TYPES.map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div className="cr-form-row">
                <label className="cr-check">
                  <input
                    type="checkbox"
                    checked={form.bidirectional}
                    onChange={(e) => setForm({ ...form, bidirectional: e.target.checked })}
                  />
                  双向关系
                </label>
                <input
                  className="cr-input cr-input-note"
                  placeholder="备注（可选）"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  aria-label="关系备注"
                />
              </div>
              <div className="cr-form-actions">
                <button className="cr-ghost" onClick={() => setShowForm(false)} disabled={saving}>
                  <X size={12} /> 取消
                </button>
                <button className="cr-primary" onClick={() => void submit()} disabled={saving} aria-label="确认新增关系">
                  <Check size={12} /> {saving ? '保存中…' : '保存'}
                </button>
              </div>
            </div>
          )}

          {related.length === 0 ? (
            <div className="cr-empty">暂无关系记录。点击「新增」建立师徒 / 亲属 / 敌对等关系。</div>
          ) : (
            <ul className="cr-list">
              {related.map(({ relation, outgoing, otherName }) => (
                <li key={relation.id} className="cr-item">
                  <span className="cr-item-main">
                    <b className="cr-name">{otherName}</b>
                    <span className="cr-type">{relation.relation_type}</span>
                    {relation.bidirectional
                      ? <span className="cr-dir">双向</span>
                      : <span className="cr-dir">{outgoing ? '→' : '←'}</span>}
                    {relation.note && <span className="cr-note">{relation.note}</span>}
                  </span>
                  <button className="cr-icon" title="删除关系" onClick={() => void removeRelation(relation.id)}>
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="cr-appearances">
          {loading ? (
            <div className="cr-empty">正在检索全书正文…</div>
          ) : (appearances?.length ?? 0) === 0 ? (
            <div className="cr-empty">正文中未找到「{character.name}」的出场记录。</div>
          ) : (
            <>
              <div className="cr-appear-summary">
                共 <b>{appearanceMeta.total}</b> 次提及，出现于 <b>{appearances!.length}</b> 章
                {appearanceMeta.first !== null && (
                  <>（第 {appearanceMeta.first} 章 ~ 第 {appearanceMeta.last} 章）</>
                )}
              </div>
              <ul className="cr-list">
                {appearances!.map((a) => (
                  <li key={a.chapter_id} className="cr-appear-item">
                    <button
                      className="cr-appear-head"
                      onClick={() => void handleRequestChapterSwitch(a.chapter_id)}
                      title="跳转到该章节"
                    >
                      <ArrowRight size={11} />
                      第{a.chapter_number}章 {a.chapter_title}
                      <span className="cr-appear-count">{a.count} 次</span>
                    </button>
                    <p className="cr-appear-snippet">
                      {a.snippetMatchStart >= 0 ? (
                        <>
                          {a.snippet.slice(0, a.snippetMatchStart)}
                          <mark>{a.snippet.slice(a.snippetMatchStart, a.snippetMatchStart + a.snippetMatchLength)}</mark>
                          {a.snippet.slice(a.snippetMatchStart + a.snippetMatchLength)}
                        </>
                      ) : a.snippet}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
